import React, { useRef, useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { AuthLang } from './AuthModal';
import { t as tI18n } from '../utils/i18n';
import { runDepthNormalMaps } from '../services/depthNormalService';

type DepthModalProps = {
  isOpen: boolean;
  onClose: () => void;
  language: AuthLang;
  userEmail?: string | null;
  /** 主界面当前原图，便于一键作为输入 */
  projectImage?: string | null;
};

const DepthModal: React.FC<DepthModalProps> = ({
  isOpen,
  onClose,
  language,
  userEmail,
  projectImage,
}) => {
  const tK = (key: Parameters<typeof tI18n>[0]) => tI18n(key, language);
  const zh = language === 'zh';
  const fileRef = useRef<HTMLInputElement>(null);

  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [depthImage, setDepthImage] = useState<string | null>(null);
  const [normalImage, setNormalImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prevIsOpenRef = useRef(false);
  /** 每次打开「深度与法线」时自动载入当前项目原图（与识别流程同源；关闭再开会重置预览） */
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (justOpened && projectImage) {
      setSourceImage(projectImage);
      setDepthImage(null);
      setNormalImage(null);
      setError(null);
    }
  }, [isOpen, projectImage]);

  const isTestMode = import.meta.env.VITE_DEPTH_NORMAL_TEST_MODE === 'true';
  const canGenerate =
    !!sourceImage && !isGenerating && (isTestMode || !!userEmail?.trim());

  const dimsFromDataUrl = useCallback((dataUrl: string) => {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
      img.onerror = () => reject(new Error('load image failed'));
      img.src = dataUrl;
    });
  }, []);

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !f.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      setSourceImage(r);
      setDepthImage(null);
      setNormalImage(null);
      setError(null);
    };
    reader.readAsDataURL(f);
  };

  const useProjectImage = () => {
    if (!projectImage) return;
    setSourceImage(projectImage);
    setDepthImage(null);
    setNormalImage(null);
    setError(null);
  };

  const handleGenerate = async () => {
    if (!sourceImage) {
      alert(zh ? '请先载入图片' : 'Please load an image first.');
      return;
    }
    if (!isTestMode && !userEmail?.trim()) {
      alert(zh ? '请先登录后再使用（需扣积分）' : 'Please sign in (credits required).');
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const { width, height } = await dimsFromDataUrl(sourceImage);
      const out = await runDepthNormalMaps(sourceImage, { width, height }, userEmail ?? undefined);
      setDepthImage(out.depthImage);
      setNormalImage(out.normalImage);
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[2500] flex flex-col bg-black text-white"
          style={{
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="h-16 border-b border-white/10 flex items-center justify-between px-4 shrink-0 bg-black/80 backdrop-blur-md">
            <h2 className="text-white font-bold text-lg tracking-tight uppercase shrink-0">
              {tK('depth_normal_panel')}
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="p-2 bg-white/10 hover:bg-white/20 text-white transition-all rounded-[5px]"
                aria-label={tK('tone_mapping_close')}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 flex-col lg:flex-row overflow-hidden">
            <aside className="w-full lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 p-6 overflow-y-auto bg-black/50">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
              <div className="space-y-4">
                <div
                  className={`relative rounded-xl border border-white/10 bg-black/30 aspect-[4/3] flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#D7CDA1]/40 transition-colors`}
                  onClick={() => fileRef.current?.click()}
                >
                  {sourceImage ? (
                    <img src={sourceImage} alt="" className="absolute inset-0 w-full h-full object-contain" />
                  ) : (
                    <span className="text-white/40 text-sm px-4 text-center">{tK('depth_load_source')}</span>
                  )}
                </div>
                {projectImage ? (
                  <button
                    type="button"
                    onClick={useProjectImage}
                    className="w-full py-2 text-xs border border-white/20 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    {tK('depth_use_project_image')}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className="w-full py-4 bg-[#D7CDA1] text-[#101820] font-bold text-sm tracking-wider uppercase rounded-[9px] hover:bg-[#c9c08f] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isGenerating ? (zh ? '生成中…' : '…') : tK('depth_generate')}
                </button>
                <p className="text-[10px] text-white/45 leading-relaxed">{tK('depth_runninghub_hint')}</p>
                {error ? (
                  <p className="text-[11px] text-red-400/90 break-words whitespace-pre-wrap">{error}</p>
                ) : null}
              </div>
            </aside>

            <main className="flex-1 min-h-0 p-4 lg:p-6 flex flex-col gap-4 overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-[280px]">
                <div className="flex flex-col min-h-0 rounded-xl border border-white/10 bg-black/20 overflow-hidden">
                  <div className="text-[11px] text-[#D7CDA1] uppercase tracking-wider px-3 py-2 border-b border-white/10 shrink-0">
                    {tK('depth_map_label')}
                  </div>
                  <div className="flex-1 min-h-[200px] flex items-center justify-center bg-black/40">
                    {depthImage ? (
                      <img src={depthImage} alt="" className="max-w-full max-h-[min(60vh,520px)] object-contain" />
                    ) : (
                      <span className="text-white/30 text-sm">—</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col min-h-0 rounded-xl border border-white/10 bg-black/20 overflow-hidden">
                  <div className="text-[11px] text-[#D7CDA1] uppercase tracking-wider px-3 py-2 border-b border-white/10 shrink-0">
                    {tK('normal_map_label')}
                  </div>
                  <div className="flex-1 min-h-[200px] flex items-center justify-center bg-black/40">
                    {normalImage ? (
                      <img src={normalImage} alt="" className="max-w-full max-h-[min(60vh,520px)] object-contain" />
                    ) : (
                      <span className="text-white/30 text-sm">—</span>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DepthModal;
